import { useState } from "react";
import { MskuPrepDetail, PackingType } from "@boxem/core-api-schema";

import styles from "./fba-shipment-items-list.module.css";

import { removeItemFromBatch } from "@/api/fulfillment";
import { FbaBatchItem, useShipmentContext } from "@/contexts/ShipmentContext";
import { useInventoryContext } from "@/contexts/InventoryContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import Pill from "@/components/Pill/Pill";
import Flex from "@/components/Flex/Flex";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";

import FbaShipmentListItem from "../FbaShipmentListItem/FbaShipmentListItem";
import FbaBatchItemDrawer from "../FbaBatchItemDrawer/FbaBatchItemDrawer";

type FbaShipmentItemsListProps = {};

const FbaShipmentItemsList = ({}: FbaShipmentItemsListProps) => {
  const { currentBatch, setCurrentBatch } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { getPrepDetailsForSKUs } = useInventoryContext();

  const [editingItem, setEditingItem] = useState<{ item: FbaBatchItem, prepDetails: MskuPrepDetail }>(null);
  const [confirmDeleteItemModal, setConfirmDeleteItemModal] = useState<{ item: FbaBatchItem, loading: boolean }>(null);

  let totalUnits = 0;
  const skus = new Set<string>();
  currentBatch.items.forEach((item) => {
    skus.add(item.sku);
    totalUnits += item.quantity;
  });

  return (
    <Flex className={styles.styledForm} direction="column">
      {!!editingItem && (
        <FbaBatchItemDrawer
          mode="UPDATE_ITEM_IN_BATCH"
          item={editingItem.item}
          prepDetails={editingItem.prepDetails}
          onClose={() => setEditingItem(null)}
        />
      )}

      {!!confirmDeleteItemModal && (
        <ConfirmModal
          open={true}
          loading={confirmDeleteItemModal.loading}
          body={`Confirm you would like to remove all ${confirmDeleteItemModal.item.quantity} units of SKU: '${confirmDeleteItemModal.item.sku}' from your shipment.`}
          confirmButtonLabel="Remove all units of SKU from Shipment"
          confirmButtonSentinment="danger"
          onClose={confirmed => {
            if (confirmed) {
              setConfirmDeleteItemModal({ ...confirmDeleteItemModal, loading: true });
              removeItemFromBatch(currentBatch.id, confirmDeleteItemModal.item.id)
                .then(() => {
                  currentBatch.items.splice(currentBatch.items.findIndex(i => i.id === confirmDeleteItemModal.item.id), 1);
                  setCurrentBatch({...currentBatch});
                })
                .catch(e => {
                  handleErrorResponseGenerically(e);
                })
                .finally(() => {
                  setConfirmDeleteItemModal(null);
                });
            } else {
              setConfirmDeleteItemModal(null);
            }
          }}
        />
      )}

      <Flex direction="column" className={styles.productsWithinShipmentsHeader}>
        <Flex>
          <span className={`strong`} style={{ marginRight: "0.5rem" }}>
            Products within shipment
          </span>
          <Pill>
            <span>{currentBatch.items.length}</span>
          </Pill>
        </Flex>
        {currentBatch.packingType === PackingType.CASE_PACKED && <span>Case-packed</span>}
      </Flex>
      {currentBatch.items.length === 0 && (
        <Flex
          direction="column"
          className={styles.emptyBox}
        >
          <img src="/images/box-add.svg" height="100" />
          <span className={"strong"}>No products selected yet</span>
          <p>Choose products from the list on the left to start building your shipment.</p>
        </Flex>
      )}
      <div className={styles.shipmentItemsContainer}>
        {currentBatch.items.map((item, index) => (
          <FbaShipmentListItem
            key={index}
            item={item}
            onEditItemClicked={async (item: FbaBatchItem) => {
              const [errors, results] = await getPrepDetailsForSKUs([item.sku]);
              if (errors) {
                handleErrorResponseGenerically(errors);
                return;
              }
              setEditingItem({ item, prepDetails: results[0] });
            }}
            onDeleteItemClicked={item => {
              setConfirmDeleteItemModal({ item, loading: false });
            }}
          />
        ))}
      </div>
      {currentBatch.items.length > 0 && (
        <div className={styles.stateBox}>
          <Flex justify="space-between" align="center">
            <span>Total units</span>
            <span className="strong">{totalUnits}</span>
          </Flex>
          <Flex justify="space-between" align="center">
            <span>Total SKUs</span>
            <span className="strong">{skus.size}</span>
          </Flex>
        </div>
      )}
    </Flex>
  );
};

export default FbaShipmentItemsList;
